import { fromShorthand, getFontFamilyName, getImage } from "@client/lib/settings";
import { type SidesValue } from "@client/lib/values/sides";
import { BackgroundImageSize, type CompositeSettings, MediaType } from "@client/types";
import { isNullOrUndefined } from "@common/lib/misc";
import * as React from "react";
import { getAssetUrl } from "./assets";
import { isColorLike } from "./color";
import { clean } from "./object";
import { STYLE_SHEET_IDS } from "./styleSheet";
import { adjustLineHeight } from "./text";

export * from "@common/lib/css";

export const getPadding = (padding: SidesValue | string | null | undefined): React.CSSProperties | undefined =>
  getPaddingInner(padding);

export const getPaddingInner = (padding: SidesValue | string | null | undefined): React.CSSProperties | undefined => {
  if (!padding) return;
  if (typeof padding === "string") return { padding };

  return {
    paddingLeft: padding.left,
    paddingRight: padding.right,
    paddingTop: padding.top,
    paddingBottom: padding.bottom
  };
};

export const getMargin = (margin: SidesValue | string | null | undefined): React.CSSProperties | undefined => {
  if (!margin) return;
  if (typeof margin === "string") return { margin };

  return {
    marginLeft: margin.left,
    marginTop: margin.top,
    marginRight: margin.right,
    marginBottom: margin.bottom
  };
};

export const getBackground = (
  value: CompositeSettings.Background | null | undefined
): React.CSSProperties | undefined => {
  if (!value) return;

  const { type, color, image, imagePosition, imageRepeat, imageSize, imageHeight, imageWidth } = value;

  if (type === "color") return { backgroundColor: color ?? "var(--color)" };
  if (type === "image") {
    const styles: React.CSSProperties = {
      backgroundColor: "transparent",
      backgroundImage: image ? `url("${encodeURI(getAssetUrl(getImage(image), MediaType.Image)!)}")` : undefined,
      backgroundRepeat: imageRepeat || undefined,
      backgroundPosition: imagePosition || undefined
    };
    if (imageSize === BackgroundImageSize.Custom) {
      styles.backgroundSize = `${imageWidth} ${imageHeight}`;
    } else {
      styles.backgroundSize = imageSize || undefined;
    }
    return styles;
  }
  return {};
};

export const getBorder = (setting: CompositeSettings.Border | null | undefined): React.CSSProperties | undefined => {
  if (!setting) return {};

  return {
    borderColor: setting.color ?? "var(--color)",
    borderStyle: setting.style,
    borderRadius: setting.radius,
    borderWidth: setting.width
  };
};

/*
{
    x,
    y,
    blur,
    spread,
    color
  }
 */
export const getBoxShadow = (value: CompositeSettings.Shadow | null | undefined): React.CSSProperties | undefined => {
  if (!value) return;

  return {
    boxShadow: getShadowValue(value)
  };
};

export const getTextDecorationStyle = (
  value: CompositeSettings.TextDecoration | null | undefined
): React.CSSProperties | undefined => {
  if (!value) return;

  return {
    textDecorationLine: value.type,
    textDecorationColor: value.color,
    textDecorationStyle: value.line
  };
};

export const getPosition = (
  setting: CompositeSettings.Position | null | undefined
): React.CSSProperties | undefined => {
  if (!setting) return {};

  const result: React.CSSProperties = {
    position: setting.type
  };
  if (setting.position) {
    const rect = fromShorthand(setting.position, "{top} {right} {bottom} {left}");
    result.top = rect.value.top;
    result.bottom = rect.value.bottom;
    result.left = rect.value.left;
    result.right = rect.value.right;
  }
  if (!isNullOrUndefined(setting.zIndex)) {
    result.zIndex = setting.zIndex;
  }
  return result;
};

export const getShadowValue = (settings: CompositeSettings.Shadow): string | undefined => {
  const offsetX = settings.x;
  const offsetY = settings.y;
  const blurRadius = settings.blur;
  const spread = settings.spread;
  const color = settings.color;

  return `${offsetX} ${offsetY} ${blurRadius} ${spread || ""} ${color}`;
};

export const getTextShadowValue = (settings: CompositeSettings.TextShadow): string | undefined => {
  const offsetX = settings.x;
  const offsetY = settings.y;
  const blurRadius = settings.blur;
  const color = settings.color;

  return `${offsetX} ${offsetY} ${blurRadius} ${color}`;
};

export const getPaddingValues = (obj: React.CSSProperties): React.CSSProperties => {
  if (obj.padding) return { padding: obj.padding };
  return {
    paddingBottom: obj.paddingBottom,
    paddingLeft: obj.paddingLeft,
    paddingTop: obj.paddingTop,
    paddingRight: obj.paddingRight
  };
};

export const getMarginValues = (obj: React.CSSProperties): React.CSSProperties => {
  if (obj.margin) return { margin: obj.margin };
  return {
    marginBottom: obj.marginBottom,
    marginLeft: obj.marginLeft,
    marginTop: obj.marginTop,
    marginRight: obj.marginRight
  };
};

export const getTextStyle = (setting: Partial<CompositeSettings.Text> | null): React.CSSProperties | undefined => {
  if (!setting) return;

  const result: React.CSSProperties = {
    color: setting.color && isColorLike(setting.color) ? setting.color : undefined,
    fontStyle: !setting.style || setting.style === "normal" ? undefined : setting.style,
    ...getTextDecorationStyle(setting.decoration),
    ...textTransformToStyle(setting.transform),
    textShadow: setting.shadow ? getTextShadowValue(setting.shadow) : undefined
  };

  if (setting.s_text === STYLE_SHEET_IDS.custom) {
    Object.assign(result, {
      fontFamily: getFontFamilyName(setting.font),
      fontSize: setting.size,
      letterSpacing: setting.letter,
      lineHeight: adjustLineHeight(setting.line),
      fontWeight: setting.weight
    });
  }

  return clean(result, (x) => isNullOrUndefined(x));
};

export const textTransformToStyle = (
  transform: CompositeSettings.TextTransform | null | undefined
): React.CSSProperties | undefined => {
  if (!transform) return;

  if (transform === "small-caps") {
    return {
      fontVariant: "small-caps"
    };
  }

  return {
    textTransform: transform
  };
};
